// lib/navigation-config.ts
// Centralized navigation configuration for the app.

export interface NavPanel {
  id: string;
  title: string;
  path: string;
}

export interface NavGroup {
  title: string;
  panels: string[]; // panel IDs
}

// All available panels in the system
export const ALL_PANELS: string[] = [
  "account",
  "home",
  "receiving",
  "receiving-2",
  "battery",
  "rework",
  "hsw",
  "glass",
  "polish-plus",
  "polish-pro",
  "polish-back",
  "polish",
  "project",
  "paint",
  "body",
  "qc",
  "body-sand",
  "search",
  "packing",
  "reprint",
  "phone-parts",
  "technician",
  "daily-report",
  "see-reports",
  "assign-jobs",
  "bins-overview",
  "my-bins",
  "bin-creation",
  "repair-summary",
  "grading",
  "calendar",
  "outside-repair",
  "supplies",
  "manage-users",
  "manage-employees",
  "timesheet",
  "searching-bodies",
  "employee-repair",
  "vacation-form",
  "technician-damages",
  "moe",
  "rocio-panel",
  "access",
  // AI-generated pages
  "ai-made/employee-monitor",
  "ai-made/phone-lookup",
  "ai-made/repair-tickets",
];

// Panels that should appear individually (not grouped)
export const INDIVIDUAL_PANELS: string[] = [
  "account",
  "home",
  "search",
  "reprint",
  "technician",
  "searching-bodies",
  "access",
  "qc"
];

// Defined groups for organizing panels
export const PANEL_GROUPS: NavGroup[] = [
  {
    title: "Receiving",
    panels: ["receiving", "receiving-2", "packing"],
  },
  {
    title: "Work Panels",
    panels: [
      "battery",
      "rework",
      "glass",
      "polish-plus",
      "polish-pro",
      "polish-back",
      "polish",
      "paint",
      "body",
      "body-sand",
      "project",
      "hsw",
    ],
  },
  {
    title: "Reports",
    panels: ["daily-report", "see-ticket", "see-reports", "repair-summary"],
  },
  {
    title: "Managing",
    panels: [
      "assign-jobs",
      "manage-users",
      "manage-employees",
      "phone-parts",
      "employee-repair",
      "rocio-panel",
    ],
  },
  {
    title: "Bins",
    panels: ["bin-creation", "my-bins", "bins-overview"],
  },
  {
    title: "Misc",
    panels: [
      "grading",
      "calendar",
      "outside-repair",
      "supplies",
      "timesheet",
      "vacation-form",
      "moe",
    ],
  },
  {
    title: "AI Generated",
    panels: ["ai-made/employee-monitor", "ai-made/phone-lookup", "ai-made/repair-tickets"],
  },
];

// Format a panel ID into a display title
export function formatPanelTitle(id: string): string {
  if (!id || id.trim() === "") return "Home";
  // Strip folder prefixes like "ai-made/"
  const baseName = id.includes("/") ? id.split("/").pop()! : id;
  return baseName
    .replace(/&/g, " & ")
    .replace(/-/g, " ")
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace("Reports", "Tickets");
}

// Build navigation structure based on user's access list
export interface BuiltNavItem {
  id: string;
  title: string;
  path: string;
  props?: Record<string, any>;
}

export interface BuiltNavGroup {
  title: string;
  items: BuiltNavItem[];
}

export function buildNavigation(
  accessList: string[],
  panelAccessData?: Record<string, Record<string, any>>
): BuiltNavGroup[] {
  const result: BuiltNavGroup[] = [];
  const usedPanels = new Set<string>();

  // Track which panels end up as individual due to single-item groups
  const individualFromGroups: string[] = [];

  // Process defined groups first
  const groupResults: BuiltNavGroup[] = [];
  for (const group of PANEL_GROUPS) {
    const allowedPanels = group.panels.filter((p) => accessList.includes(p));
    if (allowedPanels.length === 1) {
      // Single panel from a group becomes individual
      individualFromGroups.push(allowedPanels[0]);
      usedPanels.add(allowedPanels[0]);
    } else if (allowedPanels.length > 1) {
      groupResults.push({
        title: group.title,
        items: allowedPanels.map((id) => ({
          id,
          title: formatPanelTitle(id),
          path: `/${id}`,
          props: panelAccessData?.[id] ?? {},
        })),
      });
      allowedPanels.forEach((p) => usedPanels.add(p));
    }
  }

  // Build individual panels section (base + collapsed groups)
  const individualIds = [
    ...INDIVIDUAL_PANELS.filter((p) => accessList.includes(p)),
    ...individualFromGroups,
  ];

  if (individualIds.length > 0) {
    result.push({
      title: "Quick Access",
      items: individualIds.map((id) => ({
        id,
        title: formatPanelTitle(id),
        path: `/${id}`,
        props: panelAccessData?.[id] ?? {},
      })),
    });
  }

  // Add grouped panels
  result.push(...groupResults);

  return result;
}
